import type { ReactNode } from "react";
import { cn } from "../utils/cn";
import { TableCell } from "./TableCell";
import { TableHeader } from "./TableHeader";

type TableProps = {
  columns: ReactNode[];
  rows: ReactNode[][];
  state?: "default" | "loading" | "empty" | "error";
  emptyMessage?: string;
  errorMessage?: string;
  gridTemplateColumns?: string;
  columnAligns?: Array<"left" | "center" | "right">;
  headerClassName?: string;
  rowClassName?: string;
};

const columnClasses: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
  5: "grid-cols-5",
  6: "grid-cols-6",
};

export function Table({
  columns,
  rows,
  state = "default",
  emptyMessage,
  errorMessage,
  gridTemplateColumns,
  columnAligns,
  headerClassName,
  rowClassName,
}: TableProps) {
  const gridCols = columnClasses[columns.length] || "grid-cols-3";
  const gridStyle = gridTemplateColumns ? { gridTemplateColumns } : undefined;
  const getAlignClass = (index: number) => {
    const align = columnAligns?.[index] ?? "left";
    if (align === "right") return "text-right";
    if (align === "center") return "text-center";
    return "text-left";
  };
  return (
    <div className="rounded-md border border-border bg-surface shadow-sm">
      <div
        className={cn(
          "grid gap-4 border-b border-border/80 px-4 py-2 bg-surface-muted",
          gridCols,
          headerClassName
        )}
        style={gridStyle}
      >
        {columns.map((c, index) => (
          <TableHeader key={`${c}-${index}`} className={getAlignClass(index)}>
            {c}
          </TableHeader>
        ))}
      </div>
      {state === "loading" ? (
        <div className="p-4 space-y-3">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="h-8 rounded-md border border-border bg-surface-muted" />
          ))}
        </div>
      ) : null}
      {state === "error" ? (
        <div className="p-4 text-sm text-danger">{errorMessage ?? "Erreur de chargement."}</div>
      ) : null}
      {state === "empty" ? (
        <div className="p-4 text-sm text-muted">{emptyMessage ?? "Aucune donnée disponible."}</div>
      ) : null}
      {state === "default"
        ? rows.map((row, index) => (
            <div
              key={index}
              className={cn(
                "grid gap-4 px-4 py-3 hover:bg-surface-subtle transition duration-150 ease-out",
                gridCols,
                rowClassName
              )}
              style={gridStyle}
            >
              {row.map((cell, cellIndex) => (
                <TableCell key={cellIndex} className={getAlignClass(cellIndex)}>
                  {cell}
                </TableCell>
              ))}
            </div>
          ))
        : null}
    </div>
  );
}
